'use client'

import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@/contexts/AuthContext'

const API_URL = process.env.NEXT_PUBLIC_API_URL

export type UserRole = 'regular' | 'beta' | 'admin'

let cachedRole: { role: UserRole; at: number } | null = null
const CACHE_TTL = 300_000 // 5 min

export function useAdminRole() {
  const { user, isLoading: authLoading } = useAuth()
  const [role, setRole] = useState<UserRole>(cachedRole?.role ?? 'regular')
  // Stay loading until auth is ready AND we've resolved the role. Otherwise
  // pages briefly flash "You do not have admin access" while user is still null.
  const [roleResolved, setRoleResolved] = useState(() => {
    if (authLoading) return false
    if (!user) return true
    return Boolean(cachedRole && Date.now() - cachedRole.at < CACHE_TTL)
  })

  const fetchRole = useCallback(async () => {
    // Auth still hydrating — keep pages in a loading state.
    if (authLoading) {
      setRoleResolved(false)
      return
    }

    // Session confirmed absent — not an admin.
    if (!user) {
      setRole('regular')
      setRoleResolved(true)
      return
    }

    if (cachedRole && Date.now() - cachedRole.at < CACHE_TTL) {
      setRole(cachedRole.role)
      setRoleResolved(true)
      return
    }

    setRoleResolved(false)
    try {
      const res = await fetch(`${API_URL}/v1/admin/me/role`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        const r = data.role as UserRole
        cachedRole = { role: r, at: Date.now() }
        setRole(r)
      } else {
        setRole('regular')
      }
    } catch {
      setRole('regular')
    } finally {
      setRoleResolved(true)
    }
  }, [authLoading, user])

  useEffect(() => {
    void fetchRole()
  }, [fetchRole])

  const isLoading = authLoading || !roleResolved

  return { role, isAdmin: role === 'admin', isBeta: role === 'beta', isLoading }
}
